import { createSelector } from '@ngrx/store';

import {
  selectGroupsTabLoading,
  selectListTabLoading,
} from '@store/trailers/lastStateTrailers.selectors';
import { selectAllTrailerGroups } from '@store/trailers/trailerGroups.selectors';
import { selectTrailersState, trailersAdapter } from '@store/trailers/trailers.state';

import { TrailerItem, TrailerList } from '../../app/portal/pages/trailers/trailers.types';

/**
 * Селектор для получения состояния списка прицепов.
 */
export const selectTrailersListState = createSelector(selectTrailersState, (state) => state.list);

/**
 * Селектор для получения общего состояния загрузки данных.
 *
 * Возвращает `true`, если загрузка данных происходит либо для списка прицепов,
 * либо для списка групп прицепов.
 *
 * @returns {boolean} Флаг состояния загрузки.
 */
export const selectGlobalTrailersLoading = createSelector(
  selectListTabLoading,
  selectGroupsTabLoading,
  (listLoading, groupsLoading) => listLoading || groupsLoading,
);

const { selectAll, selectEntities, selectIds } =
  trailersAdapter.getSelectors(selectTrailersListState);

/**
 * Селектор для получения всех прицепов.
 */
export const selectAllTrailers = selectAll;

/**
 * Селектор для получения сущностей прицепов.
 */
export const selectTrailersEntities = selectEntities;

/**
 * Селектор для получения идентификаторов прицепов.
 */
export const selectTrailersIds = selectIds;

/**
 * Селектор для получения состояния загрузки списка прицепов.
 */
export const selectLoadingTrailersList = createSelector(
  selectTrailersState,
  (state) => state.loading,
);

/**
 * Селектор для получения прицепа по идентификатору.
 *
 * @param {string | number} id - Идентификатор прицепа.
 */
export const selectTrailerById = (id: string | number) =>
  createSelector(selectTrailersEntities, (entities) => entities[id]);

/**
 * Селектор для получения списка прицепов по массиву идентификаторов.
 *
 * @param {Array<number>} ids - Массив идентификаторов прицепов.
 */
export const selectTrailersByIds = (ids: number[]) =>
  createSelector(selectTrailersEntities, (entities): TrailerList =>
    ids.map((id) => entities[id]).filter((trailer): trailer is TrailerItem => Boolean(trailer)),
  );

/**
 * Селектор для получения прицепов, которые не входят ни в одну группу.
 */
export const selectUngroupedTrailers = createSelector(
  selectAllTrailers,
  selectAllTrailerGroups,
  (trailers, groups) => {
    const groupedTrailerIds = new Set(groups.flatMap((group) => group.tiedTrailers ?? []));
    return trailers.filter((trailer) => !groupedTrailerIds.has(trailer.id));
  },
);

/**
 * Селектор для получения прицепов, привязанных к конкретному объекту.
 *
 * @param {number} objectId - Идентификатор объекта.
 */
export const selectTrailersByObjectId = (objectId: number) =>
  createSelector(selectAllTrailers, (trailers) =>
    trailers.filter((trailer) => trailer.object?.id === objectId),
  );
